import dayjs from "newui/utils/dayjs";
import { Tooltip } from "@chakra-ui/react";
import { cn, timezoneChecker } from "utils/helpers";
import { RFX_STATUS } from "utils/constants";
import { Rfx, RfxStatus } from "newui/features/Rfx/types/rfx.types";

const SUBMISSION_STATUS_WARNINGS = [
	RFX_STATUS.ACCEPTED,
	RFX_STATUS.INVITED,
	RFX_STATUS.IN_PROGRESS,
];

const DEFAULT_RED_DAYS = 7;
const DEFAULT_WARNING_DAYS = 14;

export const DATE_ONLY_FORMAT = "dddd, D MMMM YYYY";
export const LONG_DATE_TIME_FORMAT = "dddd, D MMMM YYYY hh:mm A z";

/**
 * Replace the dayjs `z` timezone token with the human abbreviation we store
 * (e.g. "AWST"). dayjs' `z` renders the runtime's Intl short name, which can be
 * "GMT+8" on slim-ICU environments; showing the stored code keeps the display
 * consistent with the timezone control. The abbreviation is bracketed so dayjs
 * treats it as a literal.
 */
export const applyTimezoneAbbreviation = (
	format: string,
	tzAbbr: string
): string => format.replace(/z/g, `[${tzAbbr}]`);

export const hasRfxSubmissionDeadlinePassed = (rfx: Rfx) => {
	if (!rfx) {
		return false;
	}

	return hasSubmissionDeadlinePassed(
		rfx.submissionDeadline && dayjs(rfx.submissionDeadline),
		rfx.submissionTimezone
	);
};

export const hasSubmissionDeadlinePassed = (
	deadlineDateTime: dayjs.Dayjs,
	deadlineTimeZone: string
): boolean => {
	if (!deadlineDateTime) {
		return false;
	}

	const timeZone = getTimeZone(deadlineTimeZone);

	const now = dayjs().tz(timeZone).second(0);
	const target = dayjs(deadlineDateTime).tz(timeZone).second(0);
	return target.diff(now, "seconds") <= 0;
};

const getTimeZone = (deadlineTimeZone: string): string => {
	return timezoneChecker(deadlineTimeZone || "AWST");
};

const getDaysRemaining = (
	deadlineDateTime: Date,
	deadlineTimeZone: string
): number => {
	if (!deadlineDateTime) {
		return null;
	}

	const timeZone = getTimeZone(deadlineTimeZone);

	const now = dayjs().tz(timeZone).startOf("day");
	const target = dayjs(deadlineDateTime).tz(timeZone).startOf("day");
	return target.diff(now, "days");
};

const getColorClassNames = (
	daysRemaining: number,
	submissionStatus: RfxStatus,
	warningDays: number
): string => {
	if (!SUBMISSION_STATUS_WARNINGS.includes(submissionStatus)) {
		return "";
	}

	if (daysRemaining <= DEFAULT_RED_DAYS) {
		return "text-red-500";
	} else if (daysRemaining <= warningDays) {
		return "text-orange-500";
	}
	return "";
};

const getDeadlineText = (
	daysRemaining: number,
	submissionStatus: RfxStatus,
	deadlineDate: dayjs.Dayjs,
	formattedDateTime: string,
	useTwoLines: boolean,
	className: string,
	warningDays: number,
	showTimeUnderneath: boolean,
	deadlineTimeZone: string,
	tzAbbr: string
): React.ReactNode => {
	const timeFormat = applyTimezoneAbbreviation("h:mm A z", tzAbbr);

	if (!SUBMISSION_STATUS_WARNINGS.includes(submissionStatus)) {
		return <div className={cn(className)}>{formattedDateTime}</div>;
	}

	if (daysRemaining === null) {
		return "";
	}

	const colorClasses = getColorClassNames(
		daysRemaining,
		submissionStatus,
		warningDays
	);

	const classes = cn(className, colorClasses);

	if (hasSubmissionDeadlinePassed(deadlineDate, deadlineTimeZone)) {
		return (
			<div className={classes}>
				{formattedDateTime}
				{useTwoLines && <br />}
				{showTimeUnderneath && deadlineDate.format(timeFormat)} (Past Due)
			</div>
		);
	} else if (daysRemaining === 0) {
		return (
			<div className={classes}>
				DUE TODAY {deadlineDate.format("(D MMMM)")} {useTwoLines && <br />}
				{deadlineDate.format(timeFormat)}
			</div>
		);
	} else {
		return (
			<div className={classes}>
				{formattedDateTime}
				{useTwoLines && <br />}{" "}
				{showTimeUnderneath && (
					<span className="text-[12px]">{deadlineDate.format(timeFormat)}</span>
				)}{" "}
				({daysRemaining} day{daysRemaining > 1 ? "s" : ""} remaining)
			</div>
		);
	}
};

interface SubmissionDeadlineProps {
	className?: string;
	dateFormat?: "date-only" | "long" | string;
	deadlineDateTime: Date;
	deadlineTimeZone: string;
	showTimeUnderneath?: boolean;
	submissionStatus: string;
	suppressTooltip?: boolean;
	twoLines?: boolean;
	warningDays?: number;
}

export const SubmissionDeadline: React.FC<SubmissionDeadlineProps> = ({
	className = "",
	dateFormat = "date",
	deadlineDateTime,
	deadlineTimeZone,
	showTimeUnderneath = false,
	submissionStatus,
	suppressTooltip = false,
	twoLines = true,
	warningDays = DEFAULT_WARNING_DAYS,
}) => {
	const safeTimeZone = getTimeZone(deadlineTimeZone);
	const tzAbbr = deadlineTimeZone || "AWST";
	const daysRemaining = getDaysRemaining(deadlineDateTime, deadlineTimeZone);
	const deadlineDue = dayjs(deadlineDateTime).tz(safeTimeZone);
	let displayFormat = dateFormat;

	if (dateFormat === "date") {
		displayFormat = DATE_ONLY_FORMAT;
	} else if (dateFormat === "long") {
		displayFormat = LONG_DATE_TIME_FORMAT;
	}

	const dateString = deadlineDue.format(
		applyTimezoneAbbreviation(displayFormat, tzAbbr)
	);

	const deadlineText = getDeadlineText(
		daysRemaining,
		submissionStatus,
		deadlineDue,
		dateString,
		twoLines,
		className,
		warningDays,
		showTimeUnderneath,
		safeTimeZone,
		tzAbbr
	);

	let tooltipText = "";

	if (SUBMISSION_STATUS_WARNINGS.includes(submissionStatus)) {
		if (daysRemaining === 0) {
			tooltipText = "Submission is due today!";
		} else if (daysRemaining > 0) {
			tooltipText = `Submission is due within ${daysRemaining} days.`;
		} else {
			tooltipText = "Submission deadline has passed.";
		}
	}

	return (
		<Tooltip
			textColor={"white"}
			bgColor={"darkslategrey"}
			shouldWrapChildren={true}
			label={tooltipText}
			aria-label="Submission deadline tooltip"
			isDisabled={suppressTooltip}
		>
			{deadlineText}
		</Tooltip>
	);
};
